/**
 * permitRowColorState.ts — the lab's Permit Row Colors rules, as loaded from
 * GET /api/v1/permit-row-colors (Settings ▸ Permit Library ▸ Permit Row Colors).
 *
 * A dependency-free holder so `permitLimit.ts` (the engine) and `permitRowColors.ts`
 * (the loader, stylesheet and React hook) can both read it without importing each
 * other. `null` = not loaded yet (or the API is unreachable): the result grids then
 * leave every row in its default colour. There is no built-in copy of the rules
 * (owner decision 2026-10-23) — a colour on screen always comes from the table.
 */

/** One limit a condition compares the result with. `id` = AI_LimitType Oid, or "MDL". */
export interface RowColorLimitRef {
  id: string;
  /** The limit type's CURRENT name ("MDL" for the row MDL); null when it was deleted. */
  name: string | null;
}

/** "Result <op> ANY | ALL of <limits>". `op` = an AI_Operator StoredValue. */
export interface RowColorCondition {
  op: string;
  match: 'any' | 'all';
  limits: RowColorLimitRef[];
}

/** One colour rule. Rules are evaluated in `sortOrder`; the first one met colours the row. */
export interface RowColorRule {
  id: string;
  name: string;
  /** #rrggbb */
  color: string;
  sortOrder: number;
  isActive: boolean;
  conditions: RowColorCondition[];
  /** The rule as the lab writes it — "If result ≥ MRL, AL, < MCL". */
  text: string;
}

let rules: RowColorRule[] | null = null;
let version = 0;
const listeners = new Set<() => void>();

/** The loaded rules in evaluation order, or null when they are not loaded. */
export function getPermitRowColorRules(): RowColorRule[] | null {
  return rules;
}

/** Replace the rules (null = not loaded → rows keep their default colour) and re-render every subscriber. */
export function setPermitRowColorRules(next: RowColorRule[] | null): void {
  rules = next ? [...next].sort((a, b) => a.sortOrder - b.sortOrder) : null;
  version += 1;
  listeners.forEach((l) => l());
}

/** Bumps on every change — for useSyncExternalStore. */
export function getPermitRowColorVersion(): number {
  return version;
}

export function subscribePermitRowColors(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The CSS class a rule paints a row with — stable per rule, safe as a class name. */
export function permitRuleClass(rule: Pick<RowColorRule, 'id'>): string {
  return `plim-row-r${rule.id.replace(/[^0-9a-zA-Z]/g, '')}`;
}
